import {
  avatar,
  background,
  captions,
  card,
  host,
  rule,
  speaker,
  text,
  visualizer,
} from './helpers'
import type { TemplateBlueprint } from './types'

/**
 * `SelfPortraitLayout` (`layouts/SelfPortraitLayout.tsx`): a band in the
 * border colour with bars rising from under it, a square portrait with a
 * name strip, captions, the episode. Two title lines (portrait, landscape),
 * one (square). Intended differences: the portrait is a square `cover`
 * picture (v1 kept the image's natural aspect under a fixed width).
 * Intended difference (#547): the subtitle ends at x 810 in portrait and
 * square, and the landscape captions sit 37 px higher (their box is 530 px
 * tall, not 597), clear of the watermark.
 */
const bars = { variant: 'equalizer' as const }
const strip = { weight: 700, lineHeight: 1.37 }
const episode = { weight: 600, lineHeight: 1.5 }

export const selfPortraitBlueprint: TemplateBlueprint = {
  kind: 'audio',
  orientations: {
    portrait: [
      background(),
      card('portrait', 'band', [0, 0, 1080, 446]),
      visualizer('portrait', [610, 0, 470, 460], bars, { surface: 'border' }),
      avatar('portrait', 70, 70, 540, 'rect'),
      { ...card('portrait', 'nameStrip', [70, 610, 540, 60]), z: 21 },
      {
        ...speaker(
          'portrait',
          [70, 610, 540, 60],
          'handle',
          { size: 32, ...strip },
          { align: 'center', surface: 'border' },
        ),
        id: 'speaker',
      },
      captions('portrait', [70, 743, 940, 852]),
      rule('portrait', 'divider', [70, 1615, 940, 2], 'border', 1),
      host('portrait', 70, 1687, 163, 'rect'),
      text('portrait', 'title', [278, 1690, 732, 108], {
        size: 36,
        ...episode,
      }),
      text('portrait', 'subtitle', [278, 1798, 532, 54], {
        size: 36,
        ...episode,
      }),
    ],
    square: [
      background(),
      card('square', 'band', [0, 0, 1080, 320]),
      visualizer('square', [446, 0, 634, 360], bars, { surface: 'border' }),
      avatar('square', 50, 50, 406, 'rect'),
      { ...card('square', 'nameStrip', [50, 456, 406, 57]), z: 21 },
      {
        ...speaker(
          'square',
          [50, 456, 406, 57],
          'handle',
          { size: 30, ...strip },
          { align: 'center', surface: 'border' },
        ),
        id: 'speaker',
      },
      captions('square', [50, 536, 980, 294]),
      rule('square', 'divider', [50, 848, 980, 2], 'border', 1),
      host('square', 50, 900, 130, 'rect'),
      text('square', 'title', [230, 915, 800, 45], { size: 30, ...episode }),
      text('square', 'subtitle', [230, 960, 580, 45], { size: 30, ...episode }),
    ],
    landscape: [
      background(),
      card('landscape', 'band', [0, 0, 1920, 343]),
      visualizer('landscape', [524, 0, 1396, 360], bars, {
        surface: 'border',
      }),
      avatar('landscape', 70, 70, 454, 'rect'),
      { ...card('landscape', 'nameStrip', [70, 524, 454, 60]), z: 21 },
      {
        ...speaker(
          'landscape',
          [70, 524, 454, 60],
          'handle',
          { size: 32, ...strip },
          { align: 'center', surface: 'border' },
        ),
        id: 'speaker',
      },
      text('landscape', 'title', [70, 778, 384, 108], { size: 36, ...episode }),
      text('landscape', 'subtitle', [70, 956, 384, 54], {
        size: 36,
        ...episode,
      }),
      captions('landscape', [630, 410, 1068, 530]),
    ],
  },
}
